import { Radio, RadioGroup } from '@headlessui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData, useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import AlertSvg from '@/assets/svgs/alert-fill.svg?react'
import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { Stepper } from '@/components/Stepper.tsx'
import { DoctorCard } from '@/components/templates/DoctorCard.tsx'
import {
  ButtonGroup,
  FullScreenContainer,
  ScrollableContent,
} from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { BodyM, Header1 } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { selectClinicPath, selectDateTimePath } from '@/router/paths.ts'
import { useAppointmentStore } from '@/store.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

import { LoaderData } from './actions.ts'

const NoDoctorsWarning = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  margin: 40px 0;
  text-align: center;
`

const AlertIcon = styled(AlertSvg)`
  color: ${({ theme }) => theme.colors.common.black62};
  width: 48px;
  height: 48px;
`

const ClinicianCardRadio = styled(Radio)`
  display: block;
  border-radius: 4px;
  margin-bottom: 20px;

  &[data-checked] {
    background-color: ${({ theme }) => theme.colors.common.background};
  }
`
const SelectDoctor = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'selectDoctor',
  })
  const navigate = useNavigate()
  const { appointmentType, category, locationId } = useParams()
  const { loadTime } = useLoaderData() as LoaderData

  const getDoctorsByLocation = useAppointmentStore(
    (state) => state.getDoctorsByLocation,
  )
  const { trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.SelectDoctor,
    { loadTime },
  )

  const doctors = getDoctorsByLocation()
  const [selectedClinician, setSelectedClinician] = useState<string | null>(
    doctors[0]?.emmaId,
  )

  return (
    <ScreenWrapper>
      <FullScreenContainer>
        <Breadcrumb
          onClick={() => {
            navigate(
              selectClinicPath({
                appointmentType: appointmentType!,
                category: category!,
              }),
            )
            trackFormInteraction({
              action: 'previous',
              formFieldName: 'back-to-previous',
            })
          }}
          title={t('breadcrumbLink')}
        />
        <Header1>{t('header')}</Header1>
        <Stepper total={6} step={3} />
        <ScrollableContent>
          {doctors.length > 0 ? (
            <RadioGroup
              name="selectedClinician"
              id="selectedClinician"
              value={selectedClinician}
              onChange={(id) => {
                setSelectedClinician(id)
                trackFormInteraction({
                  action: 'click',
                  formFieldName: 'doctor',
                  formFieldValue: doctors.find((doctor) => doctor.emmaId === id)
                    ?.name,
                  formFieldValues: doctors?.map((doctor) => doctor.name),
                })
              }}
            >
              {doctors.map((doctor) => (
                <ClinicianCardRadio
                  value={doctor.emmaId}
                  key={doctor.emmaId}
                  aria-label={doctor.name}
                >
                  <DoctorCard doctor={doctor} />
                </ClinicianCardRadio>
              ))}
            </RadioGroup>
          ) : (
            <NoDoctorsWarning>
              <AlertIcon aria-hidden="true" />
              <BodyM>{t('noDoctorsFound')}</BodyM>
            </NoDoctorsWarning>
          )}
        </ScrollableContent>

        <ButtonGroup>
          {doctors.length === 0 ? (
            <StyledActionButton
              variant="Primary"
              type="submit"
              onClick={() => {
                navigate(
                  selectClinicPath({
                    appointmentType: appointmentType!,
                    category: category!,
                  }),
                )
                trackFormInteraction({
                  action: 'previous',
                  formFieldName: 'back-to-previous',
                })
              }}
            >
              {t('actions.selectAnotherLocation')}
            </StyledActionButton>
          ) : (
            <>
              <StyledActionButton
                variant="Primary"
                type="submit"
                onClick={() => {
                  navigate(
                    selectDateTimePath({
                      appointmentType: appointmentType!,
                      category: category!,
                      doctorId: selectedClinician!,
                      locationId: locationId!,
                    }),
                  )
                  trackFormInteraction({
                    action: 'next',
                    formFieldName: 'submit',
                  })
                }}
                disabled={!selectedClinician}
              >
                {t('actions.continue')}
              </StyledActionButton>
              {doctors.length > 1 && (
                <StyledActionButton
                  variant="Secondary"
                  onClick={() => {
                    navigate(
                      selectDateTimePath({
                        appointmentType: appointmentType!,
                        category: category!,
                        doctorId: 'all',
                        locationId: locationId!,
                      }),
                    )
                    trackFormInteraction({
                      action: 'next',
                      formFieldName: 'skipThisStep',
                    })
                  }}
                >
                  {t('actions.skipThisStep')}
                </StyledActionButton>
              )}
            </>
          )}
        </ButtonGroup>
      </FullScreenContainer>
    </ScreenWrapper>
  )
}

export { SelectDoctor }
